import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { logError } from "@/lib/log";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { SpecialityAccessFields } from "@/components/SpecialityAccessFields";
import { KeyRound, Plus } from "lucide-react";
import { toast } from "sonner";

const DURATIONS = [["1", "1 month"], ["3", "3 months"], ["6", "6 months"], ["12", "12 months"], ["none", "No expiry"]];
const EMPTY = { code: "", plan: "support", duration: "3", max_uses: "1", note: "", speciality: "", full_access: false };

function CodeRow({ c, onToggle }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-t border-border align-top" data-testid={`access-code-row-${c.code}`}>
        <td className="px-4 py-3 font-mono font-semibold">{c.code}{c.note && <p className="text-xs font-sans font-normal text-muted-foreground">{c.note}</p>}</td>
        <td className="px-4 py-3">{c.plan_label}{c.speciality_label && <p className="text-xs text-muted-foreground" data-testid={`access-code-speciality-${c.code}`}>{c.speciality_label}{c.full_access ? " · full library" : ""}</p>}</td>
        <td className="px-4 py-3 text-muted-foreground">{c.duration_months ? `${c.duration_months} mo` : "No expiry"}</td>
        <td className="px-4 py-3">
          <button type="button" className="underline-offset-2 hover:underline" onClick={() => setOpen((o) => !o)} data-testid={`access-code-uses-${c.code}`}>{c.uses} / {c.max_uses}</button>
        </td>
        <td className="px-4 py-3"><Badge variant="secondary" className={c.active ? "bg-emerald-100 text-emerald-800" : "bg-muted text-muted-foreground"}>{c.active ? "Active" : "Disabled"}</Badge></td>
        <td className="px-4 py-3 text-right">
          <Button size="sm" variant="ghost" onClick={() => onToggle(c)} data-testid={`access-code-toggle-${c.code}`}>{c.active ? "Disable" : "Enable"}</Button>
        </td>
      </tr>
      {open && (
        <tr className="bg-muted/30"><td colSpan={6} className="px-4 py-3 text-xs text-muted-foreground">
          {c.redemptions.length ? c.redemptions.map((r) => (
            <p key={r.user_id}>{r.name || r.email} · {r.email} · {new Date(r.at).toLocaleDateString("en-GB")}{r.expires ? ` → expires ${new Date(r.expires).toLocaleDateString("en-GB")}` : ""}</p>
          )) : "Not used yet."}
        </td></tr>
      )}
    </>
  );
}

export default function AdminAccessCodes() {
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/access-codes").then((r) => setCodes(r.data)).catch((e) => logError("access-codes", e));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/admin/access-codes", { code: form.code.trim(), plan: form.plan, duration_months: form.duration === "none" ? null : Number(form.duration), max_uses: Number(form.max_uses), note: form.note, speciality: form.speciality || null, full_access: form.full_access });
      toast.success(`Code ${form.code.trim().toUpperCase()} created`); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not create code"); }
    finally { setBusy(false); }
  };
  const toggle = async (c) => {
    try { await api.patch(`/admin/access-codes/${c.id}`, { active: !c.active }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not update"); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-access-codes">
      <p className="font-display font-semibold flex items-center gap-2 mb-1"><KeyRound className="w-4 h-4 text-accent" /> Access codes — free access without a card</p>
      <p className="text-xs text-muted-foreground mb-4">Create a code, hand it to a pilot partner or group. They redeem it on the Pricing page or in Billing and the plan unlocks immediately, reverting to free when it expires.</p>
      <form onSubmit={create} className="grid sm:grid-cols-6 gap-2 mb-5" data-testid="access-code-form">
        <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="CODE e.g. PILOT2026" required minLength={4} maxLength={32} pattern="[A-Za-z0-9_-]+" className="sm:col-span-2 font-mono" data-testid="access-code-input" />
        <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
          <SelectTrigger data-testid="access-code-plan"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="support">Ongoing Support</SelectItem><SelectItem value="enterprise">Enterprise Multi-site</SelectItem></SelectContent>
        </Select>
        <Select value={form.duration} onValueChange={(v) => setForm({ ...form, duration: v })}>
          <SelectTrigger data-testid="access-code-duration"><SelectValue /></SelectTrigger>
          <SelectContent>{DURATIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="number" min={1} max={10000} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="Max uses" data-testid="access-code-max-uses" />
        <Button type="submit" disabled={busy} data-testid="access-code-create"><Plus className="w-4 h-4 mr-1" /> Create</Button>
        <SpecialityAccessFields idPrefix="access-code" value={form} onChange={(v) => setForm({ ...form, ...v })} />
        <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note (optional) — e.g. NHS trust evaluation" maxLength={200} className="sm:col-span-6" data-testid="access-code-note" />
      </form>
      {codes.length ? (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="px-4 py-2">Code</th><th className="px-4 py-2">Plan</th><th className="px-4 py-2">Duration</th><th className="px-4 py-2">Uses</th><th className="px-4 py-2">Status</th><th className="px-4 py-2" /></tr>
            </thead>
            <tbody>{codes.map((c) => <CodeRow key={c.id} c={c} onToggle={toggle} />)}</tbody>
          </table>
        </div>
      ) : <p className="text-sm text-muted-foreground" data-testid="access-codes-empty">No codes yet.</p>}
    </div>
  );
}
